import { GetItemsProps } from 'medere-ui-framework'
import { axiosInstance } from '../../App'
import { ComboDataDTO } from '../../dto/ComboDataDTO'
import {
  defaultServiceBossDTO,
  defaultServiceDetailDTO,
  ServiceBossDTO,
  ServiceDetailDTO,
  ServiceListItemDTO,
  ServicesListQueryParams,
  ServiceSaveDTO,
} from '../../dto/services/ServicesDTO'

type UnknownRecord = Record<string, unknown>

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null
}

function toRecord(value: unknown): UnknownRecord | null {
  return isRecord(value) ? value : null
}

function toStringSafe(value: unknown): string {
  return typeof value === 'string' ? value.trim() : String(value ?? '').trim()
}

function parseNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }
  if (typeof value === 'string') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

function parsePositiveNumber(value: unknown): number | null {
  const parsed = parseNumber(value)
  return parsed != null && parsed > 0 ? parsed : null
}

function parseBoolean(value: unknown, fallback = false): boolean {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value === 1
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (normalized === 'true') return true
    if (normalized === 'false') return false
  }
  return fallback
}

function parseDate(value: unknown): Date | null {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }
  return null
}

function extractArray(rawData: unknown): unknown[] {
  if (Array.isArray(rawData)) return rawData
  const rawObj = toRecord(rawData)
  if (!rawObj) return []
  if (Array.isArray(rawObj.data)) return rawObj.data
  if (Array.isArray(rawObj.items)) return rawObj.items
  if (Array.isArray(rawObj.content)) return rawObj.content
  return []
}

function toComboDTO(item: unknown): ComboDataDTO | null {
  const rawObj = toRecord(item)
  if (!rawObj) return null

  const id = parsePositiveNumber(rawObj.id ?? rawObj.value ?? rawObj.professionalId ?? rawObj.professionalid)
  const name = toStringSafe(rawObj.name ?? rawObj.description ?? rawObj.label ?? rawObj.professionalName ?? rawObj.professionalname)
  if (id == null || name === '') return null
  return { id, name }
}

function normalizeServiceListItem(item: unknown): ServiceListItemDTO | null {
  const rawObj = toRecord(item)
  if (!rawObj) return null

  const serviceId = parsePositiveNumber(rawObj.serviceId ?? rawObj.serviceid ?? rawObj.healthService ?? rawObj.healthservice)
  if (serviceId == null) return null

  return {
    serviceId,
    externalCode: toStringSafe(rawObj.externalCode ?? rawObj.externalcode),
    serviceName: toStringSafe(rawObj.serviceName ?? rawObj.servicename),
    currentBossName: toStringSafe(rawObj.currentBossName ?? rawObj.currentbossname ?? rawObj.serviceBossName ?? rawObj.servicebossname),
    active: parseBoolean(rawObj.active, true),
  }
}

function normalizeServiceBoss(item: unknown): ServiceBossDTO | null {
  const rawObj = toRecord(item)
  if (!rawObj) return null

  const professional = toComboDTO(rawObj.professional) ?? toComboDTO({
    professionalId: rawObj.professionalId ?? rawObj.professionalid ?? rawObj.medereentity,
    professionalName: rawObj.professionalName ?? rawObj.professionalname,
  })

  if (!professional?.id) return null

  return {
    serviceBossDateId: parsePositiveNumber(rawObj.serviceBossDateId ?? rawObj.servicebossdateid ?? rawObj.serviceBossDate ?? rawObj.servicebossdate),
    professional,
    professionalName: professional.name ?? '',
    dateBegin: parseDate(rawObj.dateBegin ?? rawObj.datebegin),
    dateEnd: parseDate(rawObj.dateEnd ?? rawObj.dateend),
  }
}

function normalizeServiceDetail(rawData: unknown): ServiceDetailDTO {
  const rawObj = toRecord(rawData)
  if (!rawObj) return defaultServiceDetailDTO

  const serviceId = parsePositiveNumber(rawObj.serviceId ?? rawObj.serviceid ?? rawObj.healthService ?? rawObj.healthservice) ?? -1
  return {
    serviceId,
    externalCode: toStringSafe(rawObj.externalCode ?? rawObj.externalcode),
    serviceName: toStringSafe(rawObj.serviceName ?? rawObj.servicename),
    active: parseBoolean(rawObj.active, true),
    bosses: extractArray(rawObj.bosses ?? rawObj.serviceBosses ?? rawObj.servicebosses)
      .map((item) => normalizeServiceBoss(item))
      .filter((item): item is ServiceBossDTO => item !== null),
  }
}

function parseCreatedServiceId(rawData: unknown): number {
  const direct = parsePositiveNumber(rawData)
  if (direct != null) return direct

  const rawObj = toRecord(rawData)
  if (rawObj) {
    const candidates = [rawObj.serviceId, rawObj.serviceid, rawObj.id, rawObj.value, rawObj.healthService, rawObj.healthservice]
    for (const candidate of candidates) {
      const parsed = parsePositiveNumber(candidate)
      if (parsed != null) return parsed
    }
  }

  throw new Error('No se pudo obtener el id del servicio guardado')
}

function formatLocalDate(value: Date | null | undefined): string | null {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return null
  return value.toISOString().slice(0, 10)
}

function extractEffectiveFilters(filterDTO: unknown, cardFilterDTO: unknown): unknown {
  const pick = (value: unknown): unknown | null => {
    const record = toRecord(value)
    if (!record) return null
    const filters = Array.isArray(record.filters) ? record.filters : []
    const filterLists = Array.isArray(record.filterLists) ? record.filterLists : []
    if (filters.length === 0 && filterLists.length === 0) return null
    return value
  }

  return pick(filterDTO) ?? pick(cardFilterDTO) ?? null
}

export { defaultServiceBossDTO }

export function useServicesService() {
function getServicesList(
  offSet: number,
  pageSize: number,
  params: ServicesListQueryParams = {},
): Promise<ServiceListItemDTO[]> {
  const filters = extractEffectiveFilters(params.filterDTO, params.cardFilterDTO)

  return axiosInstance.post('/api/secured/services/list', {
    loadConfigDTO: {
      offSet,
      pageSize,
      sortingFieldInfoList: params.multiSortMeta,
      filters,
    },
    activeFilter: params.activeFilter ?? [],
  }).then((response) => extractArray(response.data)
    .map((item) => normalizeServiceListItem(item))
    .filter((item): item is ServiceListItemDTO => item !== null))
}

function getServiceById(serviceId: number): Promise<ServiceDetailDTO> {
  return axiosInstance.get(`/api/secured/services/${serviceId}`)
    .then((response) => normalizeServiceDetail(response.data))
}

function saveService(payload: ServiceSaveDTO): Promise<number> {
  if (payload.serviceId != null && payload.serviceId > 0) {
    return axiosInstance.put(`/api/secured/services/${payload.serviceId}`, payload)
      .then(() => payload.serviceId as number)
  }

  return axiosInstance.post('/api/secured/services', payload)
    .then((response) => parseCreatedServiceId(response.data))
}

function deleteService(serviceId: number): Promise<void> {
  return axiosInstance.delete(`/api/secured/services/${serviceId}`)
    .then(() => undefined)
}

function getServiceProfessionals({ filterValue, offSet, pageSize }: GetItemsProps): Promise<ComboDataDTO[]> {
  return axiosInstance.get('/api/secured/services/professionals', {
    params: {
      inputText: filterValue ?? '',
      offSet,
      pageSize,
    },
  }).then((response) => extractArray(response.data)
    .map((item) => toComboDTO(item))
    .filter((item): item is ComboDataDTO => item !== null))
}

function buildServiceSavePayload(detail: ServiceDetailDTO, bossIdsToDelete: number[]): ServiceSaveDTO {
  return {
    serviceId: detail.serviceId > 0 ? detail.serviceId : null,
    externalCode: detail.externalCode.trim(),
    serviceName: detail.serviceName.trim(),
    active: detail.active,
    bosses: detail.bosses
      .filter((boss) => boss.professional?.id != null && boss.professional.id > 0 && formatLocalDate(boss.dateBegin) != null)
      .map((boss) => ({
        serviceBossDateId: boss.serviceBossDateId ?? null,
        professionalId: boss.professional?.id as number,
        dateBegin: formatLocalDate(boss.dateBegin) as string,
        dateEnd: formatLocalDate(boss.dateEnd),
      })),
    bossIdsToDelete: bossIdsToDelete.filter((id) => id > 0),
  }
}

  return {
    getServicesList,
    getServiceById,
    saveService,
    deleteService,
    getServiceProfessionals,
    buildServiceSavePayload,
  }
}
